import {
  getAppointmentDate,
  getGroupedAppointmentsForPatient,
  toPeriodDateKey,
} from "./patientPeriodSummary";
import { getLinkedPrescriptionId } from "./patientTimelineVisitUtils";
import { getInvoiceList } from "../services/invoiceService";
import { getOrganizationName } from "./organizationUtils";
import { getInvoiceAttachment } from "./invoiceAttachmentUtils";
import { lookupRememberedDepartment } from "./invoiceDepartmentMemory";
import {
  normalizeMedicineTypeLabel,
  resolveMedicineIssueDepartmentName,
} from "./medicinePricingUtils";

export {
  getInvoiceAttachment,
  readInvoiceAttachmentMemory,
  rememberInvoiceAttachment,
  mergeInvoicesPreservingAttachments,
  resolveAttachmentUrl,
  unwrapInvoiceRecord,
} from "./invoiceAttachmentUtils";

export const DEFAULT_INVOICE_FETCH_DAYS = 90;
export const INVOICE_LIST_CACHE_TTL_MS = 5 * 60 * 1000;

/** Issued-to-patient medicine invoices (department should be medicine type). */
export const isMedicineIssueInvoice = (invoice) => {
  if (!invoice) return false;
  const type = String(invoice.invoiceType || invoice.invoice_type || "")
    .trim()
    .toUpperCase();
  return type === "MEDICINE";
};

const getTransactionInvoiceId = (tx) =>
  tx?.invoiceId ?? tx?.invoice_id ?? tx?.invoiceID ?? null;

/**
 * Build invoiceId → department label (Allopathy / Homeopathy / …)
 * from medicine OUT transactions (and optional inventory type fallback).
 */
export const buildMedicineDepartmentByInvoiceId = (
  transactions = [],
  inventoryList = []
) => {
  const inventoryTypeById = new Map();
  (inventoryList || []).forEach((inv) => {
    const id = inv?.id ?? inv?.medicineInventoryId ?? inv?.medicine_inventory_id;
    if (id == null) return;
    const type =
      normalizeMedicineTypeLabel(inv.medicineType) ||
      normalizeMedicineTypeLabel(inv.medicine_type);
    if (type) {
      inventoryTypeById.set(String(id), type);
      inventoryTypeById.set(Number(id), type);
    }
  });

  const typesByInvoice = new Map();
  (transactions || []).forEach((tx) => {
    const invoiceId = getTransactionInvoiceId(tx);
    if (invoiceId == null || invoiceId === "") return;

    const txType = String(tx.transactionType || tx.transaction_type || "")
      .trim()
      .toUpperCase();
    // Issue invoices are OUT; ignore stock IN rows that may share invoice ids
    if (txType && txType !== "OUT") return;

    let type =
      normalizeMedicineTypeLabel(tx.medicineType) ||
      normalizeMedicineTypeLabel(tx.medicine_type);

    if (!type) {
      const invId =
        tx.medicineInventoryId ??
        tx.medicine_inventory_id ??
        tx.inventoryId ??
        tx.inventory_id;
      if (invId != null) {
        type =
          inventoryTypeById.get(String(invId)) ||
          inventoryTypeById.get(Number(invId)) ||
          null;
      }
    }
    if (!type) return;

    const key = String(invoiceId);
    if (!typesByInvoice.has(key)) typesByInvoice.set(key, new Set());
    typesByInvoice.get(key).add(type);
  });

  const result = {};
  typesByInvoice.forEach((typeSet, invoiceId) => {
    const types = Array.from(typeSet);
    result[invoiceId] = types.length === 1 ? types[0] : types.join(", ");
  });
  return result;
};

/**
 * Resolve department for a MEDICINE invoice without reading invoice.department*
 * from DB — prefer transaction/inventory trace by invoice id.
 */
export const resolveMedicineInvoiceDepartmentName = (
  invoice,
  medicineDepartmentByInvoiceId = null
) => {
  if (!invoice) return null;

  const invoiceId = invoice.id ?? invoice.invoiceId ?? invoice.invoice_id;
  if (
    medicineDepartmentByInvoiceId &&
    invoiceId != null &&
    (medicineDepartmentByInvoiceId[invoiceId] ||
      medicineDepartmentByInvoiceId[String(invoiceId)])
  ) {
    return (
      medicineDepartmentByInvoiceId[invoiceId] ||
      medicineDepartmentByInvoiceId[String(invoiceId)]
    );
  }

  // Optional client-only hints (not required from backend invoice row)
  const direct =
    normalizeMedicineTypeLabel(invoice.medicineType) ||
    normalizeMedicineTypeLabel(invoice.medicine_type);
  if (direct) return direct;

  return resolveMedicineIssueDepartmentName(
    invoice.medicines || invoice.medicineDetails || invoice.items || []
  );
};

export const getDefaultInvoiceFetchRange = () => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - DEFAULT_INVOICE_FETCH_DAYS);
  const format = (d) => d.toISOString().slice(0, 10);
  return { fromDate: format(fromDate), toDate: format(toDate) };
};

export const getInvoiceCacheKey = (organizationId, fromDate, toDate, fullRange = false) =>
  fullRange
    ? `hms_invoice_list_${organizationId || "0"}_fullRange_v6`
    : `hms_invoice_list_${organizationId || "0"}_${fromDate || "all"}_${toDate || "all"}_v6`;

export const readInvoiceListCache = (cacheKey) => {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.timestamp) return null;
    if (Date.now() - parsed.timestamp > INVOICE_LIST_CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

export const writeInvoiceListCache = (cacheKey, data) => {
  try {
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch {
    // Ignore quota errors
  }
};

const PLACEHOLDER_VALUES = new Set(["n/a", "na", "null", "-", "none", "unknown"]);

const INCOMING_STOCK_INVOICE_TYPES = new Set([
  "homeopathy",
  "allopathy",
  "pathology",
]);

/** Invoice types that are not linked to a patient (donation / misc / stock). */
const NON_PATIENT_INVOICE_TYPES = new Set([
  ...INCOMING_STOCK_INVOICE_TYPES,
  "donation",
  "misc",
]);

const hasText = (value) => {
  if (value == null || value === "") return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== "" && !PLACEHOLDER_VALUES.has(normalized);
};

export const isIncomingStockInvoice = (invoice) => {
  if (!invoice) return false;
  const type = String(invoice.invoiceType || invoice.invoice_type || "")
    .trim()
    .toLowerCase();
  return INCOMING_STOCK_INVOICE_TYPES.has(type);
};

/** Stock / inventory purchase invoices (hospital buying from suppliers). */
export const isExpenseInvoice = (invoice) => isIncomingStockInvoice(invoice);

/** Patient billing, donation, misc, and other hospital income invoices. */
export const isIncomeInvoice = (invoice) =>
  Boolean(invoice) && !isIncomingStockInvoice(invoice);

export const isNonPatientInvoice = (invoice) => {
  if (!invoice) return false;
  if (isIncomingStockInvoice(invoice)) return true;
  const type = String(invoice.invoiceType || invoice.invoice_type || "")
    .trim()
    .toLowerCase();
  return NON_PATIENT_INVOICE_TYPES.has(type);
};

export const resolveInvoiceFromDisplayName = (invoice, organizationName = null) => {
  if (!invoice) return "N/A";

  if (isIncomingStockInvoice(invoice)) {
    const supplier =
      invoice.supplierName ||
      invoice.supplier_name ||
      invoice.fromName ||
      invoice.from_name;
    return hasText(supplier) ? String(supplier).trim() : "N/A";
  }

  const direct = [
    invoice.fromName,
    invoice.from_name,
    invoice.organizationName,
    invoice.organization_name,
    invoice.hospitalName,
    invoice.hospital_name,
  ].find((value) => hasText(value));

  if (direct) return String(direct).trim();

  const orgFallback = organizationName || getOrganizationName(null);
  if (hasText(orgFallback)) return String(orgFallback).trim();

  return "N/A";
};

export const resolveInvoicePatientDisplayName = (invoice, patient = null) => {
  if (!invoice) return "N/A";
  // Donation / MISC / stock invoices have no patient
  if (isNonPatientInvoice(invoice)) return "N/A";

  const raw =
    patient?.name ||
    invoice.patientName ||
    invoice.patient_name ||
    invoice.toName ||
    invoice.to_name ||
    null;

  if (!hasText(raw)) return "N/A";

  const normalized = String(raw).trim().toLowerCase();
  if (normalized === "unknown patient" || normalized === "unknown") return "N/A";

  return String(raw).trim();
};

export const normalizeApiArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const candidates = [
    value.data,
    value.content,
    value.items,
    value.invoices,
    value.results,
    value.records,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
};

export const groupAppointmentsByPatientId = (appointments) => {
  const grouped = {};
  normalizeApiArray(appointments).forEach((appointment) => {
    if (!appointment) return;
    const patientId = appointment.patientId ?? appointment.patient_id;
    const key =
      patientId != null && patientId !== ""
        ? String(patientId)
        : appointment.patientName || "unknown";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(appointment);
  });
  return grouped;
};

export const normalizeGroupedAppointments = (value) => {
  if (Array.isArray(value)) {
    return groupAppointmentsByPatientId(value);
  }
  if (!value || typeof value !== "object") {
    return {};
  }
  return value;
};

export const getInvoicePatientId = (invoice, patient = null) => {
  if (!invoice) return null;

  const nestedPatient =
    invoice.patient && typeof invoice.patient === "object" ? invoice.patient : null;
  const scalarPatient =
    typeof invoice.patient === "number" || typeof invoice.patient === "string"
      ? invoice.patient
      : null;

  const fromInvoice =
    invoice.patientId ??
    invoice.patient_id ??
    invoice.patientID ??
    scalarPatient ??
    nestedPatient?.id ??
    nestedPatient?.patientId ??
    null;

  if (fromInvoice != null && fromInvoice !== "") {
    return fromInvoice;
  }

  if (patient?.id != null && patient.id !== "") return patient.id;
  if (patient?.patientId != null && patient.patientId !== "") return patient.patientId;

  return null;
};

export const normalizeInvoiceRecord = (invoice) => {
  if (!invoice || typeof invoice !== "object") return invoice;

  const patient =
    invoice.patient && typeof invoice.patient === "object" ? invoice.patient : null;
  const doctor =
    invoice.doctor && typeof invoice.doctor === "object" ? invoice.doctor : null;
  const departmentValue = invoice.department;
  const departmentObject =
    departmentValue && typeof departmentValue === "object" ? departmentValue : null;

  const scalarDoctor =
    typeof invoice.doctor === "number" || typeof invoice.doctor === "string"
      ? invoice.doctor
      : null;

  return {
    ...invoice,
    patientId: getInvoicePatientId(invoice),
    patientName: isNonPatientInvoice(invoice)
      ? null
      : invoice.patientName ??
        invoice.patient_name ??
        patient?.name ??
        invoice.toName ??
        null,
    doctorId:
      invoice.doctorId ??
      invoice.doctor_id ??
      scalarDoctor ??
      doctor?.id ??
      doctor?.doctorId ??
      null,
    doctorName: hasText(invoice.doctorName)
      ? invoice.doctorName
      : hasText(invoice.doctor_name)
        ? invoice.doctor_name
        : hasText(doctor?.name)
          ? doctor.name
          : hasText(doctor?.doctorName)
            ? doctor.doctorName
            : null,
    departmentId:
      invoice.departmentId ??
      invoice.department_id ??
      departmentObject?.id ??
      doctor?.departmentId ??
      null,
    departmentName: hasText(invoice.departmentName)
      ? invoice.departmentName
      : hasText(invoice.department_name)
        ? invoice.department_name
        : typeof departmentValue === "string" && hasText(departmentValue)
          ? departmentValue
          : hasText(departmentObject?.name)
            ? departmentObject.name
            : hasText(departmentObject?.departmentName)
              ? departmentObject.departmentName
              : hasText(doctor?.departmentName)
                ? doctor.departmentName
                : hasText(doctor?.department)
                  ? doctor.department
                  : null,
    admissionId: invoice.admissionId ?? invoice.admission_id ?? null,
    appointmentId: invoice.appointmentId ?? invoice.appointment_id ?? null,
    prescriptionId: invoice.prescriptionId ?? invoice.prescription_id ?? null,
    attachment: getInvoiceAttachment(invoice) || null,
    particulars:
      invoice.particulars ?? invoice.comments ?? invoice.remarks ?? null,
    fromName: hasText(invoice.fromName)
      ? invoice.fromName
      : hasText(invoice.from_name)
        ? invoice.from_name
        : null,
  };
};

export const getInvoiceDoctorId = (invoice) => {
  if (!invoice) return null;
  const doctorId = invoice.doctorId ?? invoice.doctor_id;
  if (doctorId == null || doctorId === "" || Number(doctorId) === 0) return null;
  return doctorId;
};

export const getInvoiceDepartmentId = (invoice) => {
  if (!invoice) return null;
  const departmentId = invoice.departmentId ?? invoice.department_id;
  return departmentId == null || departmentId === "" ? null : departmentId;
};

export const invoiceNeedsPatientBulk = (invoices) =>
  (invoices || []).some((invoice) =>
    Boolean(
      invoice.patientId ||
        invoice.patient_id ||
        invoice.admissionId ||
        invoice.admission_id ||
        invoice.toName
    )
  );

export const invoiceNeedsAdmissionBulk = (invoices) =>
  (invoices || []).some((invoice) => {
    if (invoice.admissionId || invoice.admission_id) return true;
    if (String(invoice.billType || "").toUpperCase() === "IPD") return true;
    return Boolean(
      (invoice.patientId || invoice.patient_id) &&
        !getInvoiceDoctorId(invoice) &&
        !hasText(invoice.doctorName)
    );
  });

export const invoiceNeedsAppointmentBulk = (invoices) =>
  (invoices || []).some((invoice) => {
    if (getInvoiceDoctorId(invoice) || hasText(invoice.doctorName)) return false;
    if (invoice.admissionId || invoice.admission_id) return false;
    if (String(invoice.billType || "").toUpperCase() === "IPD") return false;
    return Boolean(invoice.patientId || invoice.patient_id);
  });

export const buildIdLookupMap = (items, getId = (item) => item?.id) => {
  const map = {};
  (items || []).forEach((item) => {
    const id = getId(item);
    if (id != null) {
      map[id] = item;
      map[String(id)] = item;
    }
  });
  return map;
};

export const buildDoctorLookupMap = (doctors) => {
  const list = normalizeApiArray(doctors);
  const map = {};
  list.forEach((doctor) => {
    [
      doctor?.id,
      doctor?.userId,
      doctor?.doctorId,
      doctor?.doctor_id,
    ].forEach((id) => {
      if (id == null || id === "" || Number(id) === 0) return;
      map[id] = doctor;
      map[String(id)] = doctor;
      const numericId = Number(id);
      if (!Number.isNaN(numericId)) {
        map[numericId] = doctor;
      }
    });
  });
  return map;
};

export const buildDoctorNameLookupMap = (doctors) => {
  const map = {};
  normalizeApiArray(doctors).forEach((doctor) => {
    const name = doctor?.name || doctor?.doctorName || doctor?.fullName;
    if (!hasText(name)) return;
    map[String(name).trim().toLowerCase()] = doctor;
  });
  return map;
};

export const buildAppointmentIndexes = (groupedAppointments) => {
  const normalized = normalizeGroupedAppointments(groupedAppointments);
  const byPatientId = { ...normalized };
  const byAppointmentId = {};

  Object.values(normalized).forEach((appointments) => {
    if (!Array.isArray(appointments)) return;
    appointments.forEach((appointment) => {
      const appointmentId = appointment?.id ?? appointment?.appointmentId;
      if (appointmentId != null) {
        byAppointmentId[appointmentId] = appointment;
        byAppointmentId[String(appointmentId)] = appointment;
      }
    });
  });

  return { byPatientId, byAppointmentId };
};

export const lookupDoctorById = (doctorMap, doctorId) => {
  if (doctorId == null || doctorId === "" || !doctorMap) return null;
  return (
    doctorMap[doctorId] ||
    doctorMap[String(doctorId)] ||
    doctorMap[Number(doctorId)] ||
    null
  );
};

export const buildAdmissionMaps = (admissionsData) => {
  const admissionById = {};
  const latestAdmissionByPatientId = {};

  (admissionsData || []).forEach((admission) => {
    if (admission?.id != null) {
      admissionById[admission.id] = admission;
      admissionById[String(admission.id)] = admission;
    }
    const patientId = admission.patientId ?? admission.patient_id;
    if (patientId != null) {
      const admissionTime = new Date(
        admission.admissionDate || admission.createdAt || admission.created_at || 0
      ).getTime();
      const existing = latestAdmissionByPatientId[String(patientId)];
      const existingTime = existing
        ? new Date(
            existing.admissionDate || existing.createdAt || existing.created_at || 0
          ).getTime()
        : -1;
      if (!existing || admissionTime >= existingTime) {
        latestAdmissionByPatientId[patientId] = admission;
        latestAdmissionByPatientId[String(patientId)] = admission;
      }
    }
  });

  return { admissionById, latestAdmissionByPatientId };
};

export const isOpdConsultationRegistrationInvoice = (invoice) => {
  const type = String(invoice?.invoiceType || invoice?.invoice_type || "")
    .trim()
    .toUpperCase();
  return type === "CONSULTATION" || type === "REGISTRATION" || type === "OPD";
};

const getAppointmentPrescriptionIdFromRecord = (appointment) =>
  getLinkedPrescriptionId(appointment);

export const findBestAppointmentForInvoice = (
  invoice,
  groupedAppointments,
  appointmentById = {},
  patientIdOverride = null
) => {
  const appointmentId = invoice?.appointmentId ?? invoice?.appointment_id;
  if (appointmentId != null) {
    const linkedAppointment =
      appointmentById[appointmentId] || appointmentById[String(appointmentId)];
    if (linkedAppointment) return linkedAppointment;
  }

  const patientId = patientIdOverride ?? getInvoicePatientId(invoice);
  if (!patientId) return null;

  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, patientId);
  if (!appointments.length) return null;

  const invoiceDateKey = toPeriodDateKey(
    invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
  );

  const isCancelledOrRejected = (appointment) => {
    if (appointment?.isCancel === true || appointment?.is_cancel === true) return true;
    const status = String(
      appointment?.status || appointment?.appointmentStatus || ""
    )
      .trim()
      .toUpperCase();
    return (
      status === "CANCELLED" ||
      status === "CANCELED" ||
      status === "REJECTED"
    );
  };

  let sameDayAppointment = null;
  let sameDayBestTime = -1;
  let latestAppointment = null;
  let latestTime = -1;
  let cancelledSameDayAppointment = null;
  let cancelledSameDayBestTime = -1;
  let cancelledLatestAppointment = null;
  let cancelledLatestTime = -1;

  appointments.forEach((appointment) => {
    const appointmentDate = getAppointmentDate(appointment);
    const appointmentTime = appointmentDate?.getTime() ?? -1;
    const cancelled = isCancelledOrRejected(appointment);

    if (cancelled) {
      if (appointmentTime > cancelledLatestTime) {
        cancelledLatestTime = appointmentTime;
        cancelledLatestAppointment = appointment;
      }
      if (invoiceDateKey) {
        const appointmentDateKey = toPeriodDateKey(appointmentDate);
        if (
          appointmentDateKey === invoiceDateKey &&
          appointmentTime > cancelledSameDayBestTime
        ) {
          cancelledSameDayBestTime = appointmentTime;
          cancelledSameDayAppointment = appointment;
        }
      }
      return;
    }

    if (appointmentTime > latestTime) {
      latestTime = appointmentTime;
      latestAppointment = appointment;
    }

    if (!invoiceDateKey) return;
    const appointmentDateKey = toPeriodDateKey(appointmentDate);
    if (appointmentDateKey === invoiceDateKey && appointmentTime > sameDayBestTime) {
      sameDayBestTime = appointmentTime;
      sameDayAppointment = appointment;
    }
  });

  return (
    sameDayAppointment ??
    cancelledSameDayAppointment ??
    latestAppointment ??
    cancelledLatestAppointment ??
    null
  );
};

export const getAppointmentPrescriptionIdForInvoice = (
  invoice,
  groupedAppointments,
  appointmentById = {},
  patientIdOverride = null
) =>
  getAppointmentPrescriptionIdFromRecord(
    findBestAppointmentForInvoice(
      invoice,
      groupedAppointments,
      appointmentById,
      patientIdOverride
    )
  );

export const getAppointmentDoctorIdForInvoice = (
  invoice,
  groupedAppointments,
  appointmentById = {},
  patientIdOverride = null
) => {
  const appointmentId = invoice?.appointmentId ?? invoice?.appointment_id;
  if (appointmentId != null) {
    const linkedAppointment =
      appointmentById[appointmentId] || appointmentById[String(appointmentId)];
    const linkedDoctorId = linkedAppointment?.doctorId ?? linkedAppointment?.doctor_id;
    if (linkedDoctorId != null && linkedDoctorId !== "" && Number(linkedDoctorId) !== 0) {
      return linkedDoctorId;
    }
  }

  const patientId = patientIdOverride ?? getInvoicePatientId(invoice);
  if (!patientId) return null;

  const appointments = getGroupedAppointmentsForPatient(groupedAppointments, patientId);
  if (!appointments.length) return null;

  const invoiceDateKey = toPeriodDateKey(invoice?.date ?? invoice?.createdAt ?? invoice?.created_at);

  const isCancelledOrRejected = (appointment) => {
    if (appointment?.isCancel === true || appointment?.is_cancel === true) return true;
    const status = String(
      appointment?.status || appointment?.appointmentStatus || ""
    )
      .trim()
      .toUpperCase();
    return (
      status === "CANCELLED" ||
      status === "CANCELED" ||
      status === "REJECTED"
    );
  };

  const hasValidDoctorId = (doctorId) =>
    doctorId != null && doctorId !== "" && Number(doctorId) !== 0;

  let sameDayDoctorId = null;
  let sameDayBestTime = -1;
  let latestDoctorId = null;
  let latestTime = -1;
  // Fallback when the only same-day / latest visit is cancelled (still keep doctor on export).
  let cancelledSameDayDoctorId = null;
  let cancelledSameDayBestTime = -1;
  let cancelledLatestDoctorId = null;
  let cancelledLatestTime = -1;

  appointments.forEach((appointment) => {
    const doctorId = appointment?.doctorId ?? appointment?.doctor_id;
    if (!hasValidDoctorId(doctorId)) return;

    const appointmentDate = getAppointmentDate(appointment);
    const appointmentTime = appointmentDate?.getTime() ?? -1;
    const cancelled = isCancelledOrRejected(appointment);

    if (cancelled) {
      if (appointmentTime > cancelledLatestTime) {
        cancelledLatestTime = appointmentTime;
        cancelledLatestDoctorId = doctorId;
      }
      if (invoiceDateKey) {
        const appointmentDateKey = toPeriodDateKey(appointmentDate);
        if (
          appointmentDateKey === invoiceDateKey &&
          appointmentTime > cancelledSameDayBestTime
        ) {
          cancelledSameDayBestTime = appointmentTime;
          cancelledSameDayDoctorId = doctorId;
        }
      }
      return;
    }

    if (appointmentTime > latestTime) {
      latestTime = appointmentTime;
      latestDoctorId = doctorId;
    }

    if (!invoiceDateKey) return;
    const appointmentDateKey = toPeriodDateKey(appointmentDate);
    if (appointmentDateKey === invoiceDateKey && appointmentTime > sameDayBestTime) {
      sameDayBestTime = appointmentTime;
      sameDayDoctorId = doctorId;
    }
  });

  return (
    sameDayDoctorId ??
    cancelledSameDayDoctorId ??
    latestDoctorId ??
    cancelledLatestDoctorId
  );
};

export const processInvoiceRows = (
  invoiceData,
  {
    doctorsData = [],
    departmentsData = [],
    patientsData = [],
    admissionsData = [],
    groupedAppointments = {},
    assignmentsByAdmissionId = {},
    organizationName = null,
    medicineDepartmentByInvoiceId = null,
  },
  parseDateTimeToLocalIsoString
) => {
  const invoices = dedupeInvoicesById(
    normalizeApiArray(invoiceData).map(normalizeInvoiceRecord)
  );
  const doctors = normalizeApiArray(doctorsData);
  const departments = normalizeApiArray(departmentsData);
  const patients = normalizeApiArray(patientsData);
  const admissions = normalizeApiArray(admissionsData);

  const doctorMap = buildDoctorLookupMap(doctors);
  const doctorNameMap = buildDoctorNameLookupMap(doctors);
  const departmentMap = buildIdLookupMap(departments);
  const patientMap = buildIdLookupMap(patients);
  const { byPatientId: appointmentsByPatientId, byAppointmentId } =
    buildAppointmentIndexes(groupedAppointments);
  const { admissionById, latestAdmissionByPatientId } = buildAdmissionMaps(admissions);

  const getAdmissionForInvoice = (invoice, patient) => {
    const admissionId = invoice.admissionId || invoice.admission_id;
    if (admissionId != null) {
      return admissionById[admissionId] || admissionById[String(admissionId)] || null;
    }

    // OPD / consultation invoices are not tied to IPD admissions. Do not fall back
    // to the patient's latest admission doctor (common cause of wrong doctor on report).
    const billType = String(invoice.billType || invoice.bill_type || "").toUpperCase();
    if (billType === "OPD" || billType === "OUTPATIENT") {
      return null;
    }
    if (!billType && !(invoice.admissionId || invoice.admission_id)) {
      // Prefer appointment-linked OPD when no explicit IPD bill type/admission
      const invoiceType = String(
        invoice.invoiceType || invoice.invoice_type || ""
      ).toUpperCase();
      if (
        invoiceType === "CONSULTATION" ||
        invoiceType === "REGISTRATION" ||
        invoiceType === "OPD"
      ) {
        return null;
      }
    }

    const patientId =
      getInvoicePatientId(invoice, patient) ??
      patient?.id ??
      invoice.patientId ??
      invoice.patient_id ??
      invoice.patientID;
    if (patientId != null) {
      return (
        latestAdmissionByPatientId[patientId] ||
        latestAdmissionByPatientId[String(patientId)] ||
        null
      );
    }
    return null;
  };

  const getDoctorIdFromAdmission = (admission, admissionId) => {
    const assignment =
      admission?.assignment ||
      (admissionId != null
        ? assignmentsByAdmissionId[admissionId] ||
          assignmentsByAdmissionId[String(admissionId)]
        : null);

    if (!admission && !assignment) return null;

    return (
      admission?.doctorId ??
      admission?.doctor_id ??
      assignment?.doctorId ??
      assignment?.doctor_id ??
      null
    );
  };

  const findPatientForInvoice = (invoice) => {
    // Donation / MISC / stock — do not infer a patient from toName/fromName
    if (isNonPatientInvoice(invoice)) return null;

    const invoicePatientId = getInvoicePatientId(invoice);
    if (invoicePatientId != null && invoicePatientId !== "") {
      const byId =
        patientMap[invoicePatientId] || patientMap[String(invoicePatientId)];
      if (byId) return byId;
    }

    const admissionId = invoice.admissionId || invoice.admission_id;
    if (admissionId != null) {
      const admission =
        admissionById[admissionId] || admissionById[String(admissionId)];
      const admissionPatientId = admission?.patientId ?? admission?.patient_id;
      if (admissionPatientId != null) {
        const byAdmission =
          patientMap[admissionPatientId] ||
          patientMap[String(admissionPatientId)];
        if (byAdmission) return byAdmission;
      }
    }

    const possiblePatientName =
      invoice.patientName || invoice.patient_name || invoice.toName || invoice.to_name;
    if (!possiblePatientName) return null;

    const invoicePatientName = String(possiblePatientName).toLowerCase().trim();
    if (!invoicePatientName || invoicePatientName === "n/a" || invoicePatientName === "null") {
      return null;
    }

    const exactMatch = patients.find(
      (p) => p.name && p.name.toLowerCase().trim() === invoicePatientName
    );
    if (exactMatch) return exactMatch;

    return (
      patients.find(
        (p) =>
          p.name &&
          (p.name.toLowerCase().trim().includes(invoicePatientName) ||
            invoicePatientName.includes(p.name.toLowerCase().trim()))
      ) || null
    );
  };

  const lookupDoctor = (doctorId) => {
    if (doctorId == null || doctorId === "" || Number(doctorId) === 0) return null;
    return lookupDoctorById(doctorMap, doctorId);
  };

  const resolveDoctorForInvoice = (invoice, patient, admission, resolvedPatientId) => {
    const remembered = lookupRememberedDepartment(null, {
      invoiceId: invoice?.id ?? invoice?.invoiceId,
      appointmentId: invoice?.appointmentId ?? invoice?.appointment_id,
      patientId: resolvedPatientId ?? getInvoicePatientId(invoice),
    });
    const billType = String(invoice.billType || invoice.bill_type || "").toUpperCase();
    const invoiceType = String(
      invoice.invoiceType || invoice.invoice_type || ""
    ).toUpperCase();
    const hasIpdAdmission = Boolean(
      invoice.admissionId || invoice.admission_id
    );
    const isOpdBill =
      !hasIpdAdmission &&
      (billType === "OPD" ||
        billType === "OUTPATIENT" ||
        (!billType &&
          ["CONSULTATION", "REGISTRATION", "OPD", "MEDICINE"].includes(
            invoiceType
          )));

    const appointmentDoctorId = getAppointmentDoctorIdForInvoice(
      invoice,
      appointmentsByPatientId,
      byAppointmentId,
      resolvedPatientId
    );

    // OPD: appointment doctor is source of truth (matches Out Patients assigned doctor).
    // Stale invoice.doctorId / invoice.doctorName often point at another doctor.
    const candidateDoctorIds = isOpdBill
      ? [
          appointmentDoctorId,
          getInvoiceDoctorId(invoice),
          remembered?.doctorId ?? remembered?.doctor_id,
        ]
      : [
          getInvoiceDoctorId(invoice),
          getDoctorIdFromAdmission(admission, invoice.admissionId || invoice.admission_id),
          appointmentDoctorId,
          remembered?.doctorId ?? remembered?.doctor_id,
          patient?.doctorId ?? patient?.doctor_id,
        ];

    for (const doctorId of candidateDoctorIds) {
      const doctor = lookupDoctor(doctorId);
      if (doctor) return doctor;
    }

    const doctorNameKey = String(
      invoice.doctorName || invoice.doctor_name || ""
    )
      .trim()
      .toLowerCase();
    if (doctorNameKey && doctorNameMap[doctorNameKey]) {
      return doctorNameMap[doctorNameKey];
    }

    return null;
  };

  const resolveDepartmentForInvoice = (invoice, doctor, admission) => {
    const rememberedDepartment = lookupRememberedDepartment(null, {
      invoiceId: invoice?.id ?? invoice?.invoiceId,
      appointmentId: invoice?.appointmentId ?? invoice?.appointment_id,
      patientId: getInvoicePatientId(invoice),
    });
    const rememberedDepartmentId = rememberedDepartment?.departmentId;
    if (rememberedDepartmentId != null) {
      return (
        departmentMap[rememberedDepartmentId] ||
        departmentMap[String(rememberedDepartmentId)] ||
        {
          id: rememberedDepartmentId,
          name: rememberedDepartment?.departmentName ?? null,
        }
      );
    }

    const directDepartmentId = getInvoiceDepartmentId(invoice);
    if (directDepartmentId != null) {
      return (
        departmentMap[directDepartmentId] ||
        departmentMap[String(directDepartmentId)] ||
        {}
      );
    }

    const admissionId = invoice.admissionId || invoice.admission_id;
    const assignment =
      admission?.assignment ||
      (admissionId != null
        ? assignmentsByAdmissionId[admissionId] ||
          assignmentsByAdmissionId[String(admissionId)]
        : null);
    const assignmentDepartmentId = assignment?.departmentId ?? assignment?.department_id;
    if (assignmentDepartmentId != null) {
      return (
        departmentMap[assignmentDepartmentId] ||
        departmentMap[String(assignmentDepartmentId)] ||
        {}
      );
    }

    const doctorDepartmentId = doctor?.departmentId ?? doctor?.department_id;
    if (doctorDepartmentId != null) {
      return (
        departmentMap[doctorDepartmentId] ||
        departmentMap[String(doctorDepartmentId)] ||
        {}
      );
    }

    if (doctor?.departmentIds?.length) {
      const deptId = doctor.departmentIds[0];
      return departmentMap[deptId] || departmentMap[String(deptId)] || {};
    }

    if (assignment?.departmentName || assignment?.department?.name) {
      return {
        name: assignment.departmentName || assignment.department?.name,
      };
    }

    return {};
  };

  const resolveDoctorDisplayName = (invoice, doctor) => {
    // Prefer the resolved doctor (appointment-backed for OPD) over stale invoice.doctorName.
    if (doctor?.name) return doctor.name;
    if (doctor?.doctorName) return doctor.doctorName;
    if (doctor?.fullName) return doctor.fullName;
    if (hasText(invoice.doctorName)) return invoice.doctorName;
    if (hasText(invoice.doctor_name)) return invoice.doctor_name;
    return "N/A";
  };

  const resolveDepartmentDisplayName = (invoice, doctor, department, admission) => {
    const admissionId = invoice.admissionId || invoice.admission_id;
    const assignment =
      admission?.assignment ||
      (admissionId != null
        ? assignmentsByAdmissionId[admissionId] ||
          assignmentsByAdmissionId[String(admissionId)]
        : null);

    const invoiceType = String(
      invoice.invoiceType || invoice.invoice_type || ""
    ).toUpperCase();
    const isOpdType = ["CONSULTATION", "REGISTRATION", "OPD"].includes(invoiceType);
    const preferDoctorDepartment =
      isOpdType || isMedicineIssueInvoice(invoice);

    // OPD / medicine issue: prefer consulting doctor's department over stale
    // invoice.department / remembered booking from another visit.
    const rememberedDepartmentName = lookupRememberedDepartment(null, {
      invoiceId: invoice?.id ?? invoice?.invoiceId,
      appointmentId: invoice?.appointmentId ?? invoice?.appointment_id,
      patientId: getInvoicePatientId(invoice),
    })?.departmentName;

    const candidates = preferDoctorDepartment
      ? [
          department?.name,
          department?.departmentName,
          doctor?.departmentName,
          doctor?.department,
          rememberedDepartmentName,
          invoice.departmentName,
          invoice.department_name,
          typeof invoice.department === "string" ? invoice.department : null,
          assignment?.departmentName,
          assignment?.department?.name,
        ]
      : [
          rememberedDepartmentName,
          invoice.departmentName,
          invoice.department_name,
          typeof invoice.department === "string" ? invoice.department : null,
          department?.name,
          department?.departmentName,
          assignment?.departmentName,
          assignment?.department?.name,
          doctor?.departmentName,
          doctor?.department,
        ];

    const resolved = candidates.find((value) => hasText(value));
    return resolved || "N/A";
  };

  return invoices.map((invoice) => {
    const resolvedCreatedAt =
      invoice?.createdAt ?? invoice?.created_at ?? invoice?.date ?? null;
    const normalizedCreatedAt = parseDateTimeToLocalIsoString(resolvedCreatedAt);

    const patient = findPatientForInvoice(invoice);
    const resolvedPatientId = getInvoicePatientId(invoice, patient);
    const admission = getAdmissionForInvoice(invoice, patient);
    const doctor = resolveDoctorForInvoice(
      invoice,
      patient,
      admission,
      resolvedPatientId
    );
    const department = resolveDepartmentForInvoice(invoice, doctor, admission);

    const existingPrescriptionId =
      invoice.prescriptionId ?? invoice.prescription_id ?? null;
    const resolvedPrescriptionId =
      existingPrescriptionId ??
      (isOpdConsultationRegistrationInvoice(invoice)
        ? getAppointmentPrescriptionIdForInvoice(
            invoice,
            groupedAppointments,
            byAppointmentId,
            resolvedPatientId
          )
        : null);

    const existingAppointmentId =
      invoice.appointmentId ?? invoice.appointment_id ?? null;
    let resolvedAppointmentId = existingAppointmentId;
    if (
      !resolvedAppointmentId &&
      isOpdConsultationRegistrationInvoice(invoice)
    ) {
      const matched = findBestAppointmentForInvoice(
        invoice,
        groupedAppointments,
        byAppointmentId,
        resolvedPatientId
      );
      resolvedAppointmentId = matched?.id ?? matched?.appointmentId ?? null;
    }

    return {
      ...invoice,
      patientId: resolvedPatientId ?? invoice.patientId ?? invoice.patient_id ?? null,
      appointmentId: resolvedAppointmentId,
      prescriptionId: resolvedPrescriptionId,
      doctorName: resolveDoctorDisplayName(invoice, doctor),
      department: resolveDepartmentDisplayName(invoice, doctor, department, admission),
      patientName: resolveInvoicePatientDisplayName(invoice, patient),
      fromName: resolveInvoiceFromDisplayName(invoice, organizationName),
      createdAt: normalizedCreatedAt || resolvedCreatedAt,
    };
  });
};

export const EMPTY_INVOICE_PERIOD_SUMMARY = {
  total: 0,
  totalAmount: 0,
  totalPatients: 0,
  new: 0,
  returning: 0,
  byDepartment: [],
};

export const formatInvoiceAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0";
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const padDatePart = (value) => String(value).padStart(2, "0");

const toLocalDateKey = (date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;

export const getCalendarMonthBounds = (refDate = new Date()) => {
  const date = refDate instanceof Date ? refDate : new Date(refDate);
  const year = date.getFullYear();
  const month = date.getMonth();
  const fromDateKey = `${year}-${padDatePart(month + 1)}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const toDateKey = `${year}-${padDatePart(month + 1)}-${padDatePart(lastDay)}`;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return {
    fromDate: fromDateKey,
    toDate: toDateKey,
    fromDateKey,
    toDateKey,
    label: `${monthNames[month]} ${year}`,
  };
};

/** Indian financial year: April 1 through March 31 (summary uses FY start through today). */
export const getFinancialYearBounds = (refDate = new Date()) => {
  const date = refDate instanceof Date ? refDate : new Date(refDate);
  const year = date.getFullYear();
  const month = date.getMonth();
  const fyStartYear = month >= 3 ? year : year - 1;
  const fromDateKey = `${fyStartYear}-04-01`;
  const toDateKey = toLocalDateKey(date);

  return {
    fromDate: fromDateKey,
    toDate: toDateKey,
    fromDateKey,
    toDateKey,
    label: `FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`,
    fyStartYear,
  };
};

export const resolveInvoiceDisplayType = (invoice) => {
  if (!invoice) return "OTHER";
  if (isExpenseInvoice(invoice)) {
    const type = String(invoice.invoiceType || invoice.invoice_type || "").trim();
    return type || "Other";
  }
  if (invoice.admissionId || invoice.admission_id) return "DISCHARGE";
  const type = String(invoice.invoiceType || invoice.invoice_type || "")
    .trim()
    .toUpperCase();
  return type || "OTHER";
};

const sortTypeAmountEntries = (typeMap) =>
  Array.from(typeMap.entries())
    .map(([type, amount]) => ({ type, amount }))
    .filter((item) => item.amount !== 0)
    .sort((a, b) => b.amount - a.amount);

export const computeInvoiceAmountSummary = (invoices, { fromDateKey, toDateKey } = {}) => {
  const incomeByType = new Map();
  const expenseByType = new Map();
  let incomeTotal = 0;
  let expenseTotal = 0;

  (invoices || []).forEach((invoice) => {
    const dateKey = toPeriodDateKey(
      invoice.date ?? invoice.createdAt ?? invoice.created_at
    );
    if (!dateKey) return;
    if (fromDateKey && dateKey < fromDateKey) return;
    if (toDateKey && dateKey > toDateKey) return;

    const amount = Number(invoice?.totalAmount) || 0;
    const displayType = resolveInvoiceDisplayType(invoice);

    if (isExpenseInvoice(invoice)) {
      expenseTotal += amount;
      expenseByType.set(displayType, (expenseByType.get(displayType) || 0) + amount);
    } else if (isIncomeInvoice(invoice)) {
      incomeTotal += amount;
      incomeByType.set(displayType, (incomeByType.get(displayType) || 0) + amount);
    }
  });

  return {
    incomeTotal,
    expenseTotal,
    incomeByType: sortTypeAmountEntries(incomeByType),
    expenseByType: sortTypeAmountEntries(expenseByType),
  };
};

/** Previous calendar month bounds — used for the invoice tiles' real month-over-month trend. */
export const getPriorCalendarMonthBounds = (refDate = new Date()) => {
  const date = refDate instanceof Date ? refDate : new Date(refDate);
  const priorMonthDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  return getCalendarMonthBounds(priorMonthDate);
};

export const buildInvoiceAmountTileSummaries = (invoices, refDate = new Date()) => {
  const monthBounds = getCalendarMonthBounds(refDate);
  const fyBounds = getFinancialYearBounds(refDate);
  const priorMonthBounds = getPriorCalendarMonthBounds(refDate);

  return {
    month: {
      label: monthBounds.label,
      ...computeInvoiceAmountSummary(invoices, {
        fromDateKey: monthBounds.fromDateKey,
        toDateKey: monthBounds.toDateKey,
      }),
    },
    // Only meaningful when the prior month falls inside the same fetched range (financial
    // year to date) — if it's in the previous FY, this legitimately comes back as all zeros
    // rather than us fabricating a comparison from data we don't have.
    priorMonth: {
      label: priorMonthBounds.label,
      ...computeInvoiceAmountSummary(invoices, {
        fromDateKey: priorMonthBounds.fromDateKey,
        toDateKey: priorMonthBounds.toDateKey,
      }),
    },
    financialYear: {
      label: fyBounds.label,
      ...computeInvoiceAmountSummary(invoices, {
        fromDateKey: fyBounds.fromDateKey,
        toDateKey: fyBounds.toDateKey,
      }),
    },
  };
};

export const computeInvoicePeriodSummary = (invoices, patientCounts = null) => {
  const departmentMap = new Map();
  const typeMap = new Map();
  let total = 0;
  let totalAmount = 0;

  const bump = (map, key) => {
    if (!map.has(key)) {
      map.set(key, {
        department: key,
        type: key,
        total: 0,
        totalAmount: 0,
      });
    }
    return map.get(key);
  };

  (invoices || []).forEach((invoice) => {
    total += 1;
    const amount = Number(invoice?.totalAmount) || 0;
    totalAmount += amount;

    const department = invoice?.department || invoice?.departmentName || "Unknown";
    const deptStats = bump(departmentMap, department);
    deptStats.total += 1;
    deptStats.totalAmount += amount;

    const type = resolveInvoiceDisplayType(invoice) || "Other";
    const typeStats = bump(typeMap, type);
    typeStats.total += 1;
    typeStats.totalAmount += amount;
  });

  const sortBreakdown = (values) =>
    Array.from(values).sort(
      (a, b) => b.totalAmount - a.totalAmount || b.total - a.total
    );

  return {
    total,
    totalAmount,
    totalPatients: patientCounts?.total ?? patientCounts?.totalPatients ?? 0,
    new: patientCounts?.new ?? 0,
    returning: patientCounts?.returning ?? 0,
    byDepartment: sortBreakdown(departmentMap.values()),
    byType: sortBreakdown(typeMap.values()),
  };
};

export const processInvoiceRowsLite = (
  invoiceData,
  parseDateTimeToLocalIsoString,
  organizationName = null,
  medicineDepartmentByInvoiceId = null
) =>
  normalizeApiArray(invoiceData).map((rawInvoice) => {
    const invoice = normalizeInvoiceRecord(rawInvoice);
    const resolvedCreatedAt =
      invoice?.createdAt ?? invoice?.created_at ?? invoice?.date ?? null;
    const normalizedCreatedAt = parseDateTimeToLocalIsoString(resolvedCreatedAt);

    const rememberedDepartment = lookupRememberedDepartment(null, {
      invoiceId: invoice?.id ?? invoice?.invoiceId,
      appointmentId: invoice?.appointmentId ?? invoice?.appointment_id,
      patientId: getInvoicePatientId(invoice),
    });

    return {
      ...invoice,
      doctorName: hasText(invoice.doctorName) ? invoice.doctorName : "N/A",
      department: hasText(rememberedDepartment?.departmentName)
        ? rememberedDepartment.departmentName
        : hasText(invoice.departmentName)
          ? invoice.departmentName
          : hasText(invoice.department)
            ? invoice.department
            : "N/A",
      patientName: resolveInvoicePatientDisplayName(invoice),
      fromName: resolveInvoiceFromDisplayName(invoice, organizationName),
      createdAt: normalizedCreatedAt || resolvedCreatedAt,
    };
  });

/** Drop duplicate invoice ids (keeps first complete-looking record). */
export const dedupeInvoicesById = (invoices = []) => {
  const seen = new Map();
  const withoutId = [];

  (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
    const id = invoice?.id ?? invoice?.invoiceId ?? invoice?.invoice_id;
    if (id == null || id === "") {
      withoutId.push(invoice);
      return;
    }
    const key = String(id);
    if (!seen.has(key)) {
      seen.set(key, invoice);
      return;
    }
    // Prefer the row that still has attachment / doctor / type details.
    const existing = seen.get(key);
    const existingScore =
      (getInvoiceAttachment(existing) ? 2 : 0) +
      (existing?.doctorId || existing?.doctor_id || existing?.doctorName ? 1 : 0);
    const nextScore =
      (getInvoiceAttachment(invoice) ? 2 : 0) +
      (invoice?.doctorId || invoice?.doctor_id || invoice?.doctorName ? 1 : 0);
    if (nextScore > existingScore) {
      seen.set(key, invoice);
    }
  });

  return [...seen.values(), ...withoutId];
};

export const fetchPeriodInvoicesForSummary = async (fromDate, toDate) => {
  const isMultiDay = Boolean(fromDate && toDate && fromDate !== toDate);

  // Multi-day: merge fullRange + date-scoped lists. Either API alone can omit rows.
  const fetches = [];
  if (isMultiDay) {
    fetches.push(getInvoiceList({ fullRange: true }).catch(() => []));
    fetches.push(
      getInvoiceList({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }).catch(() => [])
    );
  } else {
    fetches.push(
      getInvoiceList({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }).catch(() => [])
    );
  }

  const results = await Promise.all(fetches);
  let invoices = [];
  results.forEach((data) => {
    invoices.push(...normalizeApiArray(data).map(normalizeInvoiceRecord));
  });

  if (!invoices.length && (fromDate || toDate) && !isMultiDay) {
    const data = await getInvoiceList({ fullRange: true }).catch(() => []);
    invoices = normalizeApiArray(data).map(normalizeInvoiceRecord);
  }

  invoices = dedupeInvoicesById(invoices);

  if (fromDate || toDate) {
    const startKey = fromDate || null;
    const endKey = toDate || fromDate || null;
    invoices = invoices.filter((invoice) => {
      const dateKey = toPeriodDateKey(
        invoice?.date ?? invoice?.createdAt ?? invoice?.created_at
      );
      if (!dateKey) return false;
      if (startKey && dateKey < startKey) return false;
      if (endKey && dateKey > endKey) return false;
      return true;
    });
  }

  return invoices;
};
